import { getWidgetAuthHeaders } from '../session/widget-session.js';
import {
  buildWidgetAnalyticsEnvelope,
  type WidgetAnalyticsEnvelope,
} from './build-event-envelope.js';
import {
  buildWidgetAnalyticsUrl,
  getWidgetAnalyticsConfig,
} from './configure-analytics.js';
import { pushGtmWidgetEvent } from './push-gtm-event.js';
import { ensureAnalyticsVisitorId } from './resolve-analytics-visitor-id.js';
import type { WidgetEventName, WidgetEventPayload } from './widget-event-types.js';
import { buildWidgetAnalyticsEventsBody, resolveAnalyticsSlugSync } from './widget-events-api.js';

const MAX_QUEUE = 200;

const queue: WidgetAnalyticsEnvelope[] = [];
let flushInFlight = false;
let flushDebounceTimer: ReturnType<typeof setTimeout> | null = null;

export function getWidgetAnalyticsQueueSize(): number {
  return queue.length;
}

function scheduleDebouncedFlush(): void {
  const cfg = getWidgetAnalyticsConfig();
  if (!cfg.flushDebounceMs || cfg.flushDebounceMs <= 0) return;

  if (flushDebounceTimer) {
    clearTimeout(flushDebounceTimer);
  }

  flushDebounceTimer = setTimeout(() => {
    flushDebounceTimer = null;
    void flushWidgetAnalytics();
  }, cfg.flushDebounceMs);
}

function logQueuedEvent(event: WidgetEventName): void {
  const cfg = getWidgetAnalyticsConfig();
  if (!cfg.debug || !cfg.backendEnabled) return;

  const flushHints = [
    'закрытие вкладки / переход в фон (visibility: hidden)',
    'TeoWidget.flushAnalytics()',
  ];
  if (cfg.flushDebounceMs) {
    flushHints.push(`~${cfg.flushDebounceMs}ms после последнего события`);
  }

  console.info(
    `[Teo Widget ANALYTICS] queued: ${event} (${queue.length} pending). POST /api/widget/events при: ${flushHints.join(' · ')}`,
  );
}

export function reportWidgetEvent<T extends WidgetEventName>(
  event: T,
  payload: WidgetEventPayload<T>,
  options?: {
    locale?: string | null;
    slug?: string | null;
    /** Runtime errors go only to our backend, never into the host site's GTM. */
    backendOnly?: boolean;
  },
): WidgetAnalyticsEnvelope<T> {
  const envelope = buildWidgetAnalyticsEnvelope(event, payload, options);
  const cfg = getWidgetAnalyticsConfig();

  if (!options?.backendOnly) {
    pushGtmWidgetEvent(envelope);
  }

  if (cfg.debug) {
    console.info('[Teo Widget ANALYTICS]', envelope.event, envelope);
  }

  if (cfg.backendEnabled && cfg.apiBase) {
    queue.push(envelope as WidgetAnalyticsEnvelope);
    if (queue.length > MAX_QUEUE) {
      queue.splice(0, queue.length - MAX_QUEUE);
    }
    logQueuedEvent(event);
    scheduleDebouncedFlush();
  }

  return envelope;
}

function partitionSerializableEvents(
  events: WidgetAnalyticsEnvelope[],
): {
  serializable: WidgetAnalyticsEnvelope[];
  droppedNames: string[];
} {
  const serializable: WidgetAnalyticsEnvelope[] = [];
  const droppedNames: string[] = [];

  for (const event of events) {
    try {
      JSON.stringify(event);
      serializable.push(event);
    } catch {
      droppedNames.push(event.event);
    }
  }

  return { serializable, droppedNames };
}

export async function flushWidgetAnalytics(): Promise<void> {
  const cfg = getWidgetAnalyticsConfig();
  if (!cfg.backendEnabled || !cfg.apiBase || queue.length === 0) return;
  if (flushInFlight) return;

  const visitorId = await ensureAnalyticsVisitorId();
  if (!visitorId) {
    if (cfg.debug) {
      console.warn(
        '[Teo Widget ANALYTICS] flush skipped — visitor_id (fingerprint) unavailable',
      );
    }
    return;
  }

  flushInFlight = true;
  const batch = queue.splice(0, queue.length);

  const { serializable, droppedNames } = partitionSerializableEvents(batch);
  if (droppedNames.length > 0) {
    console.warn(
      '[Teo Widget ANALYTICS] dropped non-serializable events (would block flush):',
      droppedNames.join(', '),
    );
  }

  if (serializable.length === 0) {
    flushInFlight = false;
    return;
  }

  const slug = resolveAnalyticsSlugSync(serializable);
  if (!slug) {
    queue.unshift(...serializable);
    if (cfg.debug) {
      console.warn('[Teo Widget ANALYTICS] flush skipped — slug unavailable');
    }
    flushInFlight = false;
    return;
  }

  const requestBody = buildWidgetAnalyticsEventsBody(visitorId, serializable);
  if (!requestBody) {
    queue.unshift(...serializable);
    if (cfg.debug) {
      console.warn('[Teo Widget ANALYTICS] flush skipped — request body invalid');
    }
    flushInFlight = false;
    return;
  }

  try {
    const url = buildWidgetAnalyticsUrl(cfg.apiBase);
    const body = JSON.stringify(requestBody);
    const authHeaders = getWidgetAuthHeaders();
    const headers: Record<string, string> = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...authHeaders,
    };
    const needsAuth = Object.keys(authHeaders).length > 0;

    if (
      !needsAuth &&
      typeof navigator !== 'undefined' &&
      'sendBeacon' in navigator
    ) {
      const blob = new Blob([body], { type: 'application/json' });
      const sent = navigator.sendBeacon(url, blob);
      if (sent) {
        if (cfg.debug) {
          console.info(
            '[Teo Widget ANALYTICS] flush sent via sendBeacon',
            serializable.length,
            requestBody,
          );
        }
        return;
      }
    }

    const response = await fetch(url, {
      method: 'POST',
      credentials: 'omit',
      headers,
      body,
      keepalive: true,
    });

    if (!response.ok) {
      const detail = await response.text().catch(() => '');
      queue.unshift(...serializable);
      if (cfg.debug) {
        console.warn(
          '[Teo Widget ANALYTICS] flush HTTP',
          response.status,
          detail,
          requestBody,
        );
      }
      return;
    }

    if (cfg.debug) {
      console.info(
        '[Teo Widget ANALYTICS] flush OK',
        response.status,
        serializable.length,
        requestBody,
      );
    }
  } catch (error) {
    queue.unshift(...serializable);
    if (cfg.debug) {
      console.warn('[Teo Widget ANALYTICS] flush failed', error);
    }
  } finally {
    flushInFlight = false;
  }
}

export function installWidgetAnalyticsLifecycle(): () => void {
  if (typeof window === 'undefined') return () => {};

  const onPageHide = () => {
    void flushWidgetAnalytics();
  };

  const onVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      void flushWidgetAnalytics();
    }
  };

  window.addEventListener('pagehide', onPageHide);
  document.addEventListener('visibilitychange', onVisibilityChange);

  return () => {
    window.removeEventListener('pagehide', onPageHide);
    document.removeEventListener('visibilitychange', onVisibilityChange);
  };
}
